import {immer} from 'zustand/middleware/immer';
import type {PersistOptions} from 'zustand/middleware';
import {devtools, persist} from 'zustand/middleware';
import type {StateCreator} from 'zustand/vanilla';
import {createStore as zustandCreateStore} from 'zustand';

export type StoreOptions<S extends unknown> = {
  name: string;
  version: number;
  devtools?: boolean;
  persist?: Omit<PersistOptions<S>, 'name'>;
};

type State = unknown;
export type StoreApi<S extends State> = ReturnType<typeof createStore<S>>;
export type StoreCreator<S extends State> = Parameters<typeof createStore<S>>[0];

/**
 * Utility function to create a Zustand store with immer and devtools middleware.
 *
 * @template S - The state type.
 * @template A - The actions type.
 * @template Store - The combined state and actions type.
 * @param {StateCreator<Store, [['zustand/immer', never]]>} storeCreator - The function to create the store's state and actions.
 * @param {Options<Store>} options - The options for configuring the store.
 * @returns {StoreApi<S, A, Store>} - The Zustand store API.
 *
 *  * @example
 * // Define state and actions types
 * type MyState = {
 *   bears: number;
 * };
 *
 * type MyActions = {
 *   increasePopulation: () => void;
 *   removeAllBears: () => void;
 * };
 *
 * // Define the store creator function
 * const myStoreCreator: StateCreator<MyState & MyActions, [['zustand/immer', never]]> = (set) => ({
 *   bears: 0,
 *   increasePopulation: () => set((state) => { state.bears += 1; }),
 *   removeAllBears: () => set({ bears: 0 }),
 * });
 *
 * // Create the store
 * const useMyStore = createStore(myStoreCreator, {
 *   name: 'bearStore',
 *   version: 1,
 *   devtools: true,
 *   persist: {
 *     getStorage: () => localStorage,
 *   },
 * });
 *
 * // Using the store in a component
 * const BearCounter = () => {
 *   const bears = useMyStore((state) => state.bears);
 *   const increasePopulation = useMyStore((state) => state.increasePopulation);
 *   return (
 *     <div>
 *       <h1>{bears} around here...</h1>
 *       <button onClick={increasePopulation}>one up</button>
 *     </div>
 *   );
 * };
 */
export const createStore = <S extends State>(
  storeCreator: StateCreator<S, [['zustand/immer', never]]>,
  options: StoreOptions<S>,
) => {
  const name = `linx@${options.name}@${options.version}`;

  const myPersist: typeof persist = options.persist ? persist : (fn: any) => fn;

  return zustandCreateStore<S>()(
    devtools(
      myPersist(immer(storeCreator), {
        ...(options.persist ?? {}),
        name,
      }),
      {
        name,
        enabled: !!options.devtools || process.env.NODE_ENV === `development`,
        serialize: {
          options: {
            map: true,
          },
        },
      },
    ),
  );
};
